import { useState, useCallback, useEffect } from 'react';
import { motion as Motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router';
import { Link } from 'react-router';
import {
  Check, Copy, ExternalLink, ArrowRight, CheckCircle2, CreditCard,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth-context';
import { apiClient } from '@/lib/api-client';
import { colors, accent, FADE } from '@/lib/tokens';
import { Spinner } from '@/components/ui/spinner';
import { Card } from '@/components/ui/card';
import { useOnboarding } from '@/components/onboarding/use-onboarding';

const NODE_SNIPPET = `import { init } from '@recost-dev/node';

init({
  apiKey:    process.env.RECOST_API_KEY,
  projectId: 'YOUR_PROJECT_ID',
});`;

const PYTHON_SNIPPET = `from recost import init, RecostConfig

init(RecostConfig(
    api_key="YOUR_API_KEY",
    project_id="YOUR_PROJECT_ID",
))`;

export default function GettingStarted() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const firstName = user?.name?.split(' ')[0] ?? 'there';

  const { data: projects = [], isLoading: projectsLoading } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['dashboard-projects'],
    queryFn: () =>
      apiClient.get<{ data: { id: string; name: string }[]; pagination: unknown }>('/projects').then((r) => r.data),
  });

  const onboarding = useOnboarding({ userId: user?.id, projects });
  const { checklistSteps, completeChecklistStep, setChecklistStep, markIntroComplete, targetProject } = onboarding;

  useEffect(() => {
    if (onboarding.status === 'ready') {
      markIntroComplete();
    }
  }, [onboarding.status, markIntroComplete]);

  const [snippetLang, setSnippetLang] = useState<'node' | 'python'>('node');
  const [copied, setCopied] = useState(false);

  const allDone = checklistSteps.extensionInstalled && checklistSteps.sdkCopied && checklistSteps.dashboardExplored;
  const completedCount = [
    checklistSteps.extensionInstalled,
    checklistSteps.sdkCopied,
    checklistSteps.dashboardExplored,
  ].filter(Boolean).length;

  const copySnippet = useCallback(() => {
    const text = snippetLang === 'node' ? NODE_SNIPPET : PYTHON_SNIPPET;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      if (!checklistSteps.sdkCopied) completeChecklistStep('sdkCopied');
    });
  }, [snippetLang, checklistSteps.sdkCopied, completeChecklistStep]);

  function handleInstallExtension() {
    window.open('https://marketplace.visualstudio.com/items?itemName=recost.recost-api-analyzer', '_blank', 'noopener');
    if (!checklistSteps.extensionInstalled) completeChecklistStep('extensionInstalled');
  }

  function handleExploreDashboard() {
    completeChecklistStep('dashboardExplored');
    if (targetProject) {
      onboarding.startTour(targetProject.id);
      navigate(`/dashboard/projects/${targetProject.id}`);
    } else {
      navigate('/dashboard/projects');
    }
  }

  if (projectsLoading || onboarding.status === 'idle' || onboarding.status === 'loading') {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="min-h-full" style={{ background: colors.bgPage }}>
      <div className="w-full px-6 md:px-10 pt-8 pb-16">

        {/* Header */}
        <Motion.div {...FADE(0)} className="mb-8">
          <p className="text-xs uppercase tracking-[0.12em] mb-2" style={{ color: accent }}>Setup</p>
          <div className="flex items-end justify-between gap-4 pb-5">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: colors.textPrimary }}>
                Hey {firstName}, let's get you set up.
              </h1>
              <p className="text-sm mt-1" style={{ color: colors.textMuted }}>
                {completedCount === 3 ? 'All steps complete.' : `${completedCount} of 3 complete`}
              </p>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
              {/* Progress pips */}
              <div className="flex gap-1.5">
                {[0, 1, 2].map(i => (
                  <div
                    key={i}
                    className="w-6 h-1 rounded-sm transition-all duration-500"
                    style={{ background: i < completedCount ? accent : colors.borderDefault }}
                  />
                ))}
              </div>
              <Link
                to="/docs"
                className="inline-flex items-center gap-1.5 text-sm transition-colors"
                style={{ color: colors.textMuted, textDecoration: 'none' }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = colors.textSecondary; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = colors.textMuted; }}
              >
                View docs <ArrowRight size={13} />
              </Link>
            </div>
          </div>
          <div style={{ borderBottom: `1px solid ${colors.borderDefault}` }} />
        </Motion.div>

        {/* Plan */}
        <Card className="mb-6 overflow-hidden">
          <div className="flex items-start justify-between gap-4 px-5 py-4 sm:px-6 sm:py-5" style={{ borderBottom: `1px solid ${colors.borderSubtle}` }}>
            <div className="flex items-start gap-3">
              <CreditCard size={15} className="flex-shrink-0 mt-0.5" style={{ color: accent }} />
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-semibold" style={{ color: colors.textPrimary }}>Beta Free</span>
                  <span
                    className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                    style={{ color: accent, background: colors.accentSubtle, letterSpacing: '0.05em', textTransform: 'uppercase' }}
                  >
                    Active
                  </span>
                </div>
                <p className="text-xs" style={{ color: colors.textMuted }}>
                  6 projects, 1 API key, 100 scans per day, 200 endpoints per project.
                </p>
              </div>
            </div>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-1 text-xs flex-shrink-0 transition-colors"
              style={{ color: colors.textMuted, textDecoration: 'none' }}
              onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = colors.textSecondary; }}
              onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = colors.textMuted; }}
            >
              View plan <ArrowRight size={11} />
            </Link>
          </div>
          <div className="flex flex-wrap">
            {([
              { label: 'Projects',    value: '6' },
              { label: 'API keys',    value: '1' },
              { label: 'Scans / day', value: '100' },
              { label: 'Endpoints',   value: '200 / project' },
            ] as const).map(({ label, value }, i) => (
              <div
                key={label}
                className="px-5 py-3 sm:px-6"
                style={{ borderRight: i < 3 ? `1px solid ${colors.borderSubtle}` : undefined, flex: '1 1 auto', minWidth: 0 }}
              >
                <p className="text-[10px] uppercase tracking-[0.1em] mb-0.5" style={{ color: colors.textMuted }}>{label}</p>
                <p className="text-sm font-mono font-semibold" style={{ color: colors.textPrimary }}>{value}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Completion banner */}
        <AnimatePresence>
          {allDone && (
            <Motion.div
              key="done"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="flex items-center gap-3 px-5 py-4 rounded-md mb-6"
              style={{ background: colors.accentSubtle, border: `1px solid ${colors.accentBorder}` }}
            >
              <CheckCircle2 size={16} style={{ color: accent, flexShrink: 0 }} />
              <div className="flex-1">
                <span className="text-sm font-semibold" style={{ color: colors.textPrimary }}>You're all set. </span>
                <span className="text-sm" style={{ color: colors.textMuted }}>Head to your dashboard to explore your projects.</span>
              </div>
              <button
                onClick={() => navigate('/dashboard/projects')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all flex-shrink-0"
                style={{ background: colors.accentSubtle, border: `1px solid ${colors.accentBorder}`, color: accent }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = colors.accentHover; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = colors.accentSubtle; }}
              >
                Go to Dashboard <ArrowRight size={12} />
              </button>
            </Motion.div>
          )}
        </AnimatePresence>

        {/* Step cards */}
        <div className="space-y-2">

          {/* Step 1 — Extension */}
          <Motion.div {...FADE(0.07)} className="rounded-md p-5"
            style={{ background: colors.bgBase, border: `1px solid ${checklistSteps.extensionInstalled ? colors.accentBorder : colors.borderDefault}` }}
          >
            <div className="flex items-start gap-4">
              <button
                onClick={() => setChecklistStep('extensionInstalled', !checklistSteps.extensionInstalled)}
                aria-pressed={checklistSteps.extensionInstalled}
                aria-label={checklistSteps.extensionInstalled ? 'Mark step incomplete' : 'Mark step complete'}
                className="mt-0.5 w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-all cursor-pointer"
                style={checklistSteps.extensionInstalled
                  ? { background: accent, border: `1px solid ${accent}` }
                  : { background: 'transparent', border: `1px solid ${colors.borderDefault}` }
                }
              >
                {checklistSteps.extensionInstalled && <Check size={11} color="#000" strokeWidth={3} />}
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.1em] font-mono mb-0.5" style={{ color: checklistSteps.extensionInstalled ? accent : colors.textMuted }}>Step 01</p>
                <p className="text-sm font-semibold mb-2" style={{ color: checklistSteps.extensionInstalled ? colors.textSecondary : colors.textPrimary }}>
                  Install the VS Code Extension
                </p>
                <p className="text-sm leading-relaxed mb-5" style={{ color: colors.textMuted }}>
                  Get inline cost annotations and quick access to your project data as you code.
                </p>
                <div className="flex items-center justify-between">
                  <button
                    onClick={handleInstallExtension}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all"
                    style={{ background: colors.accentSubtle, border: `1px solid ${colors.accentBorder}`, color: accent }}
                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = colors.accentHover; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = colors.accentSubtle; }}
                  >
                    Open Marketplace <ExternalLink size={11} />
                  </button>
                  <Link
                    to="/docs/extension"
                    className="inline-flex items-center gap-1.5 text-sm transition-colors"
                    style={{ color: colors.textMuted, textDecoration: 'none' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = colors.textSecondary; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = colors.textMuted; }}
                  >
                    Quick start <ArrowRight size={13} />
                  </Link>
                </div>
              </div>
            </div>
          </Motion.div>

          {/* Step 2 — SDK */}
          <Motion.div {...FADE(0.13)} className="rounded-md p-5"
            style={{ background: colors.bgBase, border: `1px solid ${checklistSteps.sdkCopied ? colors.accentBorder : colors.borderDefault}` }}
          >
            <div className="flex items-start gap-4">
              <button
                onClick={() => setChecklistStep('sdkCopied', !checklistSteps.sdkCopied)}
                aria-pressed={checklistSteps.sdkCopied}
                aria-label={checklistSteps.sdkCopied ? 'Mark step incomplete' : 'Mark step complete'}
                className="mt-0.5 w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-all cursor-pointer"
                style={checklistSteps.sdkCopied
                  ? { background: accent, border: `1px solid ${accent}` }
                  : { background: 'transparent', border: `1px solid ${colors.borderDefault}` }
                }
              >
                {checklistSteps.sdkCopied && <Check size={11} color="#000" strokeWidth={3} />}
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.1em] font-mono mb-0.5" style={{ color: checklistSteps.sdkCopied ? accent : colors.textMuted }}>Step 02</p>
                <p className="text-sm font-semibold mb-2" style={{ color: checklistSteps.sdkCopied ? colors.textSecondary : colors.textPrimary }}>
                  Add the Middleware SDK
                </p>
                <p className="text-sm leading-relaxed mb-4" style={{ color: colors.textMuted }}>
                  Add one line to your server to start capturing live API costs.
                </p>
                <div className="rounded-lg overflow-hidden mb-5" style={{ border: `1px solid ${colors.borderDefault}` }}>
                  <div className="flex items-center px-1" style={{ background: colors.bgSubtle, borderBottom: `1px solid ${colors.borderDefault}` }}>
                    {(['node', 'python'] as const).map(lang => (
                      <button
                        key={lang}
                        onClick={() => setSnippetLang(lang)}
                        className="relative px-3 py-2 text-[11px] font-mono transition-colors cursor-pointer"
                        style={{ color: snippetLang === lang ? colors.textPrimary : colors.textMuted }}
                      >
                        {lang === 'node' ? 'Node.js' : 'Python'}
                        {snippetLang === lang && (
                          <span className="absolute inset-x-0 -bottom-px h-px" style={{ background: accent }} />
                        )}
                      </button>
                    ))}
                    <button
                      onClick={copySnippet}
                      className="ml-auto flex items-center gap-1.5 px-3 py-2 text-[11px] cursor-pointer transition-colors"
                      style={{ color: copied ? '#4ade80' : colors.textMuted }}
                    >
                      {copied ? <Check size={11} /> : <Copy size={11} />}
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                  <pre
                    className="px-4 py-3 text-[11px] font-mono leading-relaxed overflow-x-auto"
                    style={{ background: colors.bgSubtle, color: colors.textSecondary, margin: 0 }}
                  >
                    {snippetLang === 'node' ? NODE_SNIPPET : PYTHON_SNIPPET}
                  </pre>
                </div>
                <div className="flex items-center justify-between">
                  <button
                    onClick={copySnippet}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all"
                    style={{ background: colors.accentSubtle, border: `1px solid ${colors.accentBorder}`, color: accent }}
                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = colors.accentHover; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = colors.accentSubtle; }}
                  >
                    {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy snippet</>}
                  </button>
                  <Link
                    to="/docs/sdk"
                    className="inline-flex items-center gap-1.5 text-sm transition-colors"
                    style={{ color: colors.textMuted, textDecoration: 'none' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = colors.textSecondary; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = colors.textMuted; }}
                  >
                    Quick start <ArrowRight size={13} />
                  </Link>
                </div>
              </div>
            </div>
          </Motion.div>

          {/* Step 3 — Dashboard */}
          <Motion.div {...FADE(0.19)} className="rounded-md p-5"
            style={{ background: colors.bgBase, border: `1px solid ${checklistSteps.dashboardExplored ? colors.accentBorder : colors.borderDefault}` }}
          >
            <div className="flex items-start gap-4">
              <button
                onClick={() => setChecklistStep('dashboardExplored', !checklistSteps.dashboardExplored)}
                aria-pressed={checklistSteps.dashboardExplored}
                aria-label={checklistSteps.dashboardExplored ? 'Mark step incomplete' : 'Mark step complete'}
                className="mt-0.5 w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-all cursor-pointer"
                style={checklistSteps.dashboardExplored
                  ? { background: accent, border: `1px solid ${accent}` }
                  : { background: 'transparent', border: `1px solid ${colors.borderDefault}` }
                }
              >
                {checklistSteps.dashboardExplored && <Check size={11} color="#000" strokeWidth={3} />}
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.1em] font-mono mb-0.5" style={{ color: checklistSteps.dashboardExplored ? accent : colors.textMuted }}>Step 03</p>
                <p className="text-sm font-semibold mb-2" style={{ color: checklistSteps.dashboardExplored ? colors.textSecondary : colors.textPrimary }}>
                  Explore the demo project
                </p>
                <p className="text-sm leading-relaxed mb-5" style={{ color: colors.textMuted }}>
                  See scan results, live telemetry, and cost breakdowns in the{targetProject ? ` ${targetProject.name}` : ' demo'} project.
                </p>
                <div className="flex items-center justify-between">
                  <button
                    onClick={handleExploreDashboard}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all"
                    style={{ background: colors.accentSubtle, border: `1px solid ${colors.accentBorder}`, color: accent }}
                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = colors.accentHover; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = colors.accentSubtle; }}
                  >
                    Open demo project <ArrowRight size={11} />
                  </button>
                  <Link
                    to="/docs"
                    className="inline-flex items-center gap-1.5 text-sm transition-colors"
                    style={{ color: colors.textMuted, textDecoration: 'none' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = colors.textSecondary; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = colors.textMuted; }}
                  >
                    View docs <ArrowRight size={13} />
                  </Link>
                </div>
              </div>
            </div>
          </Motion.div>

        </div>

      </div>
    </div>
  );
}
